/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import {
	Badge,
	Box,
	Button,
	Center,
	Checkbox,
	Container,
	Flex,
	Select,
	SimpleGrid,
	Text,
	Tooltip,
	useBreakpointValue,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { faChevronLeft, faChevronRight, faPlus, faPrint, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import { useHistory, useLocation } from 'react-router-dom';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import ListLoading from '../../components/ListLoading';
import PrintLayout from '../../components/Print/PrintLayout';
import TotalRow from '../../components/TotalRow';
import RichTextEditorViewer from '../../components/form/RichTextEditorField/viewer';
import c from '../../constant';
import useMoment from '../../hooks/useMoment';
import useStore from '../../store';
import { isUserPermitted } from '../../utils/checker';
import ListFilterModal from './components/ListFilterModal';
import NewNpModal from './components/NewNpModal';
import Statistic from './components/Statistic';
import StepTab from './components/StepTab';
import UnreadModal from './components/UnreadModal';
import { allDepartments, allOffices } from './utils';

const Q_GET_REQUESTS = gql`
	query get(
		$pagerInput: PagerInput!
		$step: String
		$unread: Boolean = false
		$isPrinted: Boolean
		$isAssistantAssignment: Boolean
		$office: String
		$department: String
		$subject: String
		$type: NpEnumType
		$number: String
		$tags: [String!]
		$dgSignedDate: String
	) {
		getNps(
			pagerInput: $pagerInput
			step: $step
			unread: $unread
			isPrinted: $isPrinted
			isAssistantAssignment: $isAssistantAssignment
			office: $office
			department: $department
			subject: $subject
			type: $type
			number: $number
			tags: $tags
			dgSignedDate: $dgSignedDate
		) {
			totalRows
			totalPages
			badge
			nps {
				id
				createdAt
				createdBy
				step
				type
				subject
				number
				department
				office
				notification {
					id
					read
				}
				completedAt
				urgent
				isPrinted
				doneFile
				checkInDgAt
				checkOutDgAt
				checkInDdgAt
				checkOutDdgAt
				assistantColor
			}
		}
	}
`;

function IntermediaryPartnerRequest() {
	const toast = useToast();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const page = urlParams.get('page') ?? null;
	const step = urlParams.get('step') ?? 'all';
	const type = urlParams.get('type') ?? null;
	const number = urlParams.get('number') ?? null;
	const subject = urlParams.get('subject') ?? null;
	const department = urlParams.get('department') ?? null;
	const office = urlParams.get('office') ?? null;
	const tags = urlParams.get('tags') ?? null;
	const dgSignedDate = urlParams.get('dgSignedDate') ?? null;
	const unread = urlParams.has('unread', true);
	const isPrinted = urlParams.get('isPrinted') === 'true' ? true : null;
	const isAssistantAssignment = urlParams.get('isAssistantAssignment') === 'true' ? true : null;
	const more = urlParams.has('more', true);
	const createModal = useDisclosure();
	const filterModal = useDisclosure();
	const departments = useMemo(() => allDepartments(), []);
	const offices = useMemo(() => allOffices(), []);
	const { toDateString } = useMoment();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { push } = useHistory();
	const { t } = useTranslation();
	const [isUserAssistant, setUserAssistant] = useState(false);
	const [isSetNpColor, setSetNpColor] = useState(false);
	const [loadData, { loading, error, data }] = useLazyQuery(Q_GET_REQUESTS);

	const showSmallScreen = useBreakpointValue({
		base: true,
		sm: true,
		md: false,
		lg: false, // >= 992px
		xl: false, // >= 1280px
	});

	const showLargeScreen = useBreakpointValue({
		base: false,
		sm: false,
		md: true,
		lg: true, // >= 992px
		xl: true, // >= 1280px
	});

	const isPrintingPermission = useMemo(() => {
		if (user) {
			const role = user?.role || '';
			const roles = role?.split(',');
			return roles?.includes('admin') || (roles?.includes('nprp') && isUserPermitted(['nprpPrintStatus'], [...(user?.nprp ?? [])]));
		}
		return false;
	}, [user]);

	useEffect(() => load(), [page, step, type, number, subject, department, office, tags, dgSignedDate, unread, isPrinted, isAssistantAssignment, more]);

	useEffect(() => data?.getNps && setTimeout(() => scrollToTop(), 500), [data]);

	useEffect(() => {
		if (user) {
			const role = user?.role || '';
			const roles = role?.split(',');
			setUserAssistant(roles?.includes('assistant'));
			setSetNpColor(roles?.includes('admin') || (roles?.includes('nprp') && isUserPermitted(['nprpSetColor'], [...(user?.nprp ?? [])])));
		}
	}, [user]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function handlePageChanged({ selected }) {
		let url = `?page=${selected + 1}`;
		if (step) {
			url += '&step=' + step;
		}
		if (type) {
			url += '&type=' + type;
		}
		if (number) {
			url += '&number=' + number;
		}
		if (subject) {
			url += '&subject=' + subject;
		}
		if (department) {
			url += '&department=' + department;
		}
		if (office) {
			url += '&office=' + office;
		}
		if (tags) {
			url += '&tags=' + encodeURIComponent(tags);
		}
		if (dgSignedDate) {
			url += '&dgSignedDate=' + dgSignedDate;
		}
		if (unread) {
			url += '&unread=' + unread;
		}
		if (isPrinted === true) {
			url += '&isPrinted=' + isPrinted;
		}
		if (isAssistantAssignment === true) {
			url += '&isAssistantAssignment=' + isAssistantAssignment;
		}
		if (more) {
			url += '&more=' + more;
		}
		push(`/note-of-presentation/list${url}`);
	}

	function tabChange(e) {
		let url = `?page=1`;
		if (e) {
			url += '&step=' + e;
		}
		if (type) {
			url += '&type=' + type;
		}
		if (number) {
			url += '&number=' + number;
		}
		if (subject) {
			url += '&subject=' + subject;
		}
		if (department) {
			url += '&department=' + department;
		}
		if (office) {
			url += '&office=' + office;
		}
		if (tags) {
			url += '&tags=' + encodeURIComponent(tags);
		}
		if (dgSignedDate) {
			url += '&dgSignedDate=' + dgSignedDate;
		}
		if (unread) {
			url += '&unread=' + unread;
		}
		if (isPrinted === true) {
			url += '&isPrinted=' + isPrinted;
		}
		if (isAssistantAssignment === true) {
			url += '&isAssistantAssignment=' + isAssistantAssignment;
		}
		if (more) {
			url += '&more=' + more;
		}
		push(`/note-of-presentation/list${url}`);
	}

	function load() {
		const moreCount = 100;
		const limit = 10;
		const offset = parseInt(page ? (page - 1) * (more === true ? moreCount : limit) : 0) || 0;
		const pagerInput = { limit: more === true ? moreCount : limit, offset: offset < 0 ? 0 : offset };
		const localTags = JSON.parse(tags) || [];
		loadData({
			variables: {
				pagerInput,
				step,
				type,
				number,
				subject,
				department,
				office,
				tags: localTags,
				dgSignedDate,
				unread,
				isPrinted: isPrinted === true ? true : null,
				isAssistantAssignment: isAssistantAssignment === true ? true : null,
			},
		});
	}

	function scrollToTop() {
		window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
	}

	return (
		<>
			<PrintLayout>
				<Container maxW="container.xl" mt="16px" mb="16px">
					<BreadcrumbComponent
						list={[
							{
								name: 'np or rp',
								path: '#',
							},
						]}
					/>
					<Center mt="5">
						<Box
							w={{ base: '100%', md: '100%' }}
							bg="white"
							borderRadius={c.borderRadius}
							border="1px solid #dbdbdb"
							boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
							pb="16px"
						>
							{showLargeScreen && (
								<Flex
									className="hidden-print responsive-header-for-table"
									bg="#FAFAFA"
									p="16px"
									pt="0"
									pb="0"
									mb="16px"
									alignItems="center"
									justifyContent="space-between"
									borderBottom="1px solid #dbdbdb"
								>
									<Flex className="panel-tab-container" pb="2px" overflow="scroll" minH="54px">
										<StepTab
											onCallback={(e) => tabChange(e)}
											initTab={step}
											data={[
												{
													label: 'Draft',
													tooltip: 'Draft',
													badge: { value: data?.getNps?.badge?.draft || 0 },
													value: 'draft',
													important: 'office',
												},
												{
													label: 'Office',
													tooltip: 'Reviewing by Office',
													badge: { value: data?.getNps?.badge?.office || 0 },
													value: 'office',
													important: 'office',
												},
												{
													label: 'Department',
													tooltip: 'Reviewing by Department',
													badge: { value: data?.getNps?.badge?.department || 0 },
													value: 'department',
													important: 'department',
												},
												{
													label: 'Deputy Director General',
													tooltip: 'Reviewing by DDG',
													badge: { value: data?.getNps?.badge?.ddg || 0 },
													value: 'ddg',
													important: 'ddg',
												},
												{
													label: 'Director General',
													tooltip: 'Reviewing by Director General',
													badge: { value: data?.getNps?.badge?.dg || 0 },
													value: 'dg',
													important: 'dg',
												},
												{
													label: 'FSA',
													tooltip: 'FSA',
													badge: { value: data?.getNps?.badge?.fsa || 0 },
													value: 'fsa',
													important: 'fsa',
												},
												{
													label: 'Done',
													tooltip: 'Done',
													// badge: { value: data?.getNps?.badge?.done || 0 },
													value: 'done',
													important: '',
												},
												{
													label: 'All',
													tooltip: 'All',
													// badge: { value: data?.getNps?.badge?.all || 0 },
													value: 'all',
													important: '',
												},
											]}
										/>
									</Flex>
									<Flex justifyContent="center" alignItems="center">
										<UnreadModal />
										<Button
											onClick={() => window?.print()}
											leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											mr={2}
										>
											{t('Print')}
										</Button>
										<Button
											mr="2"
											leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											onMouseDown={createModal?.onOpen}
										>
											{t('Create')}
										</Button>
										<Button
											onClick={filterModal?.onOpen}
											leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
											colorScheme={
												type ||
												number ||
												subject ||
												department ||
												office ||
												tags ||
												dgSignedDate ||
												unread === true ||
												isPrinted === true ||
												isAssistantAssignment === true
													? 'yellow'
													: 'gray'
											}
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
										>
											{t('Filter')}
										</Button>
									</Flex>
								</Flex>
							)}

							{/* Responsive Select Option for Mobile */}

							{showSmallScreen && (
								<Box
									w="100%"
									bg="white"
									borderRadius={c.borderRadius}
									border="1px solid #dbdbdb"
									boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
									pb="16px"
								>
									<Flex
										className="hidden-print responsive-header-for-table"
										bg="#FAFAFA"
										p="16px"
										pt="0"
										pb="0"
										mb="16px"
										alignItems="center"
										justifyContent="space-between"
										borderBottom="1px solid #dbdbdb"
									>
										<Select size="sm" value={step} onChange={(e) => tabChange(e.target.value)}>
											<option value="draft">
												{t('Draft')} {data?.getNps?.badge?.draft >= 1 ? `(${data?.getNps?.badge?.draft})` : ''}
											</option>
											<option value="office">
												{t('Office')} {data?.getNps?.badge?.office >= 1 ? `(${data?.getNps?.badge?.office})` : ''}
											</option>
											<option value="department">
												{t('Department')} {data?.getNps?.badge?.department >= 1 ? `(${data?.getNps?.badge?.department})` : ''}
											</option>
											<option value="ddg">
												{t('Deputy Director General')} {data?.getNps?.badge?.ddg >= 1 ? `(${data?.getNps?.badge?.ddg})` : ''}
											</option>
											{/* <option value="assistant" disabled={true}>
												{t('Assistant')}
											</option> */}
											<option value="dg">
												{t('Director General')} {data?.getNps?.badge?.dg >= 1 ? `(${data?.getNps?.badge?.dg})` : ''}
											</option>
											<option value="fsa">
												{t('FSA')} {data?.getNps?.badge?.fsa >= 1 ? `(${data?.getNps?.badge?.fsa})` : ''}
											</option>
											<option value="done">{t('Done')}</option>
											<option value="all">{t('All')}</option>
										</Select>
										<Flex justifyContent="center" alignItems="center" mt={2} mb={2}>
											<SimpleGrid columns={3} spacingX={1} spacingY={2}>
												<UnreadModal />
												<Button
													onClick={() => window?.print()}
													leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
													colorScheme="gray"
													variant="solid"
													size="sm"
													borderRadius={c.borderRadius}
													border="1px solid #bdc3c7"
													mr={2}
												>
													{t('Print')}
												</Button>
												<Button
													mr="2"
													leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
													colorScheme="gray"
													variant="solid"
													size="sm"
													borderRadius={c.borderRadius}
													border="1px solid #bdc3c7"
													onMouseDown={createModal?.onOpen}
												>
													{t('Create')}
												</Button>
												<Button
													onClick={filterModal?.onOpen}
													leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
													colorScheme={number || subject || department || office || tags || unread === true ? 'yellow' : 'gray'}
													variant="solid"
													size="sm"
													borderRadius={c.borderRadius}
													border="1px solid #bdc3c7"
												>
													{t('Filter')}
												</Button>
											</SimpleGrid>
										</Flex>
									</Flex>
								</Box>
							)}
							<ListLoading
								loading={loading}
								loadedList={data?.getNps?.nps}
								totalRows={data?.getNps?.totalRows}
								totalPages={data?.getNps?.totalPages}
							>
								{({ loadedList, totalPages, totalRows }) => (
									<Box p="16px" pt="8px" pb="8px" mb="16px">
										<Table variant="simple" className="responsive-table-container">
											<Thead>
												<Tr className="td-as-th">
													<Th align="left">{t('Number')}</Th>
													<Th align="left">{t('Subject')}</Th>
													<Th align="left">{t('Department')}</Th>
													<Th align="left">{t('Office')}</Th>
													<Th align="left">{t('End At')}</Th>
													<Th align="left">{t('type')}</Th>
													<Th align="right">{t('Status')}</Th>
												</Tr>
											</Thead>
											<Tbody>
												{loadedList?.length === 0 && (
													<Tr>
														<Td colSpan={7} textAlign="center">
															{t('No records found')}
														</Td>
													</Tr>
												)}
												{loadedList?.map((e, i) => (
													<Tr
														_hover={{ bg: 'gray.200' }}
														key={`tr-${i}`}
														cursor="pointer"
														className={e?.notification?.read === false ? 'new' : 'read'}
														onMouseDown={() => push(`/note-of-presentation/detail?id=${e?.id}&type=${e?.type}`)}
													>
														<Td pt={3} pb={3} style={{ color: isSetNpColor && e?.assistantColor ? e?.assistantColor : '' }}>
															{e?.number || 'N/A'}
														</Td>
														<Td pt={3} pb={3}>
															<Box maxWidth="260px">
																<RichTextEditorViewer value={e?.subject} makeup={true} />
															</Box>
														</Td>
														<Td pt={3} pb={3}>
															{lang === 'kh' ? departments[e?.department]?.labelShortKh : departments[e?.department]?.labelShort}
														</Td>
														<Td pt={3} pb={3}>
															{lang === 'kh' ? offices[e?.office]?.labelKh : offices[e?.office]?.labelEn}
														</Td>
														<Td pt={3} pb={3}>
															{toDateString(e?.completedAt, c.dateTimeFormat) || '-'}
														</Td>
														<Td pt={3} pb={3}>
															<Flex justifyContent="flex-start" alignItems="center">
																{isUserAssistant ? (
																	e?.completedAt ? (
																		e?.doneFile ? (
																			<Tooltip label={t('Completed by done file')}>
																				<Badge colorScheme="green">{t(e?.type)}</Badge>
																			</Tooltip>
																		) : (
																			<Tooltip label={t('Completed by step')}>
																				<Badge colorScheme="purple">{t(e?.type)}</Badge>
																			</Tooltip>
																		)
																	) : (
																		<Badge>{t(e?.type)}</Badge>
																	)
																) : (
																	<Badge>{t(e?.type)}</Badge>
																)}
															</Flex>
														</Td>
														<Td pt={3} pb={3} isNumeric>
															<Flex justifyContent="flex-end" alignItems="center">
																{e?.urgent === true || e?.isPrinted === true ? (
																	<>
																		{e?.urgent === true && (
																			<Badge colorScheme="red" ml={0}>
																				{t('Urgency')}
																			</Badge>
																		)}
																		{e?.isPrinted === true && isPrintingPermission && (
																			<Badge colorScheme="green" ml={1}>
																				{t('Printed as hard copy')}
																			</Badge>
																		)}
																	</>
																) : (
																	<>-</>
																)}
															</Flex>
														</Td>
													</Tr>
												))}
											</Tbody>
											<tfoot className="responsive-footer">
												<Tr className="td-as-th">
													<Th align="left">{t('Number')}</Th>
													<Th align="left">{t('Subject')}</Th>
													<Th align="left">{t('Department')}</Th>
													<Th align="left">{t('Office')}</Th>
													<Th align="left">{t('End At')}</Th>
													<Th align="left">{t('type')}</Th>
													<Th align="right">{t('Status')}</Th>
												</Tr>
											</tfoot>
										</Table>

										{totalRows > 0 && (
											<Flex justifyContent="flex-end" direction="row">
												<TotalRow count={totalRows} mt={2} />
											</Flex>
										)}

										{totalPages > 1 && (
											<Flex alignItems="flex-end" direction="column" mt={4}>
												<ReactPaginate
													previousLabel={<FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 12, cursor: 'pointer' }} />}
													nextLabel={<FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 12, cursor: 'pointer' }} />}
													breakLabel={'...'}
													breakClassName={'break-me'}
													disableInitialCallback={true}
													pageCount={totalPages}
													marginPagesDisplayed={2}
													pageRangeDisplayed={3}
													onPageChange={handlePageChanged}
													containerClassName={'pagination'}
													activeClassName={'active'}
													initialPage={(page - 1) | 0}
												/>
											</Flex>
										)}

										<Flex className="hidden-print" justifyContent="flex-end" mt={6}>
											<Text mr="2">{t('Show more rows')}</Text>
											<Checkbox
												isChecked={more}
												onChange={(e) => {
													let url = '?page=1';
													if (step) url += '&step=' + step;
													if (number) url += '&number=' + number;
													if (subject) url += '&subject=' + subject;
													if (department) url += '&department=' + department;
													if (office) url += '&office=' + office;
													if (tags) url += '&tags=' + encodeURIComponent(tags);
													if (unread) url += '&unread=' + unread;
													if (e?.target?.checked) url += '&more=' + e?.target?.checked;
													push(`/note-of-presentation/list${url}`);
												}}
											/>
										</Flex>
									</Box>
								)}
							</ListLoading>
						</Box>
					</Center>
					<Box width={{ base: '100%', md: '20%' }}>
						<Statistic />
					</Box>
				</Container>
			</PrintLayout>

			{createModal?.isOpen && (
				<NewNpModal
					onClose={createModal?.onClose}
					onComplete={(result) => push('/note-of-presentation/detail?id=' + result?.id + '&type=' + result?.type)}
				/>
			)}

			{filterModal?.isOpen && (
				<ListFilterModal
					step={step}
					type={type}
					number={number}
					subject={subject}
					department={department}
					office={office}
					tags={JSON.parse(tags) || []}
					dgSignedDate={dgSignedDate}
					unread={unread}
					isPrinted={isPrinted}
					isAssistantAssignment={isAssistantAssignment}
					more={more}
					onClose={filterModal?.onClose}
					onCallback={(url) => push('/note-of-presentation/list' + url)}
				/>
			)}
		</>
	);
}

export default IntermediaryPartnerRequest;
